/**
 * Permission matrix — dev brief §8.
 *
 * Authorisation is `role × resource × action`, declared once here and checked
 * in Hono middleware. Role checks must never be scattered through handlers:
 * the day someone adds an endpoint and forgets one is the day a VIEWER can
 * mark a tree dead.
 *
 * Site scoping is a *second, independent* check (§9 rule 4). Passing the role
 * matrix says "a MANAGER may verify work orders"; the site check says "…but
 * not at a site they are not assigned to." Both must pass.
 */

export const ROLES = ['OWNER', 'ADMIN', 'MANAGER', 'AGRONOMIST', 'FIELD_WORKER', 'VIEWER'] as const;
export type Role = (typeof ROLES)[number];

export const RESOURCES = [
  'tenant',
  'user',
  'site',
  'block',
  'position',
  'tree',
  'tree_event',
  'attachment',
  'harvest',
  'observation',
  'measurement',
  'input_application',
  'inventory',
  'issue',
  'work_order',
  'work_order_line',
  'schedule',
  'task_type',
  'worker',
  'variety',
  'product',
  'pest_disease',
  'buyer',
  'sales_lot',
  'weather',
  'report',
  'audit_log',
] as const;
export type Resource = (typeof RESOURCES)[number];

export const ACTIONS = [
  'read',
  'create',
  'update',
  'delete',
  /** Work orders: promote to `verified`, which is what makes a record count. */
  'verify',
  /** Generate or reprint a PDF / tag sheet. */
  'print',
  /** CSV or PDF download of a list. Always audited. */
  'export',
  /** Hide a timeline entry — ADMIN and above only (§6.4). */
  'hide',
  /** Irreversible domain acts: mark dead, replace, lock position codes. */
  'destroy',
] as const;
export type Action = (typeof ACTIONS)[number];

/** How far a role's grant reaches. */
export type ScopeKind =
  /** Every site in the tenant. */
  | 'tenant'
  /** Only sites listed in `user_site`. */
  | 'site';

const ALL: readonly Action[] = ACTIONS;
const READ: readonly Action[] = ['read'];
const READ_EXPORT: readonly Action[] = ['read', 'export'];
const CRUD: readonly Action[] = ['read', 'create', 'update', 'delete'];
const CRU: readonly Action[] = ['read', 'create', 'update'];
const NONE: readonly Action[] = [];

type Matrix = Record<Role, Partial<Record<Resource, readonly Action[]>>>;

/**
 * Anything a role is not explicitly granted is denied. New resources therefore
 * default to locked, which is the correct direction to fail.
 */
const MATRIX: Matrix = {
  // Everything, including user management and destructive actions.
  OWNER: Object.fromEntries(RESOURCES.map((r) => [r, ALL])) as Matrix['OWNER'],

  // Everything except tenant configuration and tenant deletion.
  ADMIN: {
    ...(Object.fromEntries(RESOURCES.map((r) => [r, ALL])) as Matrix['ADMIN']),
    tenant: READ,
  },

  // Runs the sites they are assigned to.
  MANAGER: {
    tenant: READ,
    user: READ,
    site: READ_EXPORT,
    block: CRU,
    position: [...CRU, 'print', 'export', 'destroy'],
    tree: [...CRUD, 'export', 'destroy'],
    tree_event: ['read', 'create', 'export'],
    attachment: CRUD,
    harvest: [...CRUD, 'export'],
    observation: CRUD,
    measurement: CRUD,
    input_application: CRUD,
    inventory: [...CRU, 'export'],
    issue: [...CRUD, 'export'],
    work_order: [...CRUD, 'verify', 'print', 'export'],
    work_order_line: CRUD,
    schedule: CRUD,
    task_type: READ,
    worker: CRUD,
    variety: READ,
    product: READ,
    pest_disease: READ,
    buyer: CRU,
    sales_lot: [...CRU, 'export'],
    weather: CRU,
    report: READ_EXPORT,
    audit_log: READ,
  },

  // Records what they see in the field and plans the work — but cannot verify
  // it, manage users, or destroy anything.
  AGRONOMIST: {
    tenant: READ,
    site: READ,
    block: READ,
    position: READ_EXPORT,
    tree: CRU,
    tree_event: ['read', 'create'],
    attachment: CRU,
    harvest: CRU,
    observation: CRU,
    measurement: CRU,
    input_application: CRU,
    inventory: READ,
    issue: CRU,
    work_order: ['read', 'create', 'update', 'print', 'export'],
    work_order_line: CRU,
    schedule: READ,
    task_type: READ,
    worker: READ,
    variety: READ,
    product: READ,
    pest_disease: READ,
    buyer: NONE,
    sales_lot: NONE,
    weather: CRU,
    report: READ_EXPORT,
    audit_log: NONE,
  },

  // No login in v1 (dev brief §2). Declared now so the future mobile app is a
  // client change rather than a permissions redesign: a field worker may only
  // complete lines on work orders assigned to them.
  FIELD_WORKER: {
    site: READ,
    position: READ,
    tree: READ,
    tree_event: ['read', 'create'],
    attachment: ['read', 'create'],
    observation: ['read', 'create'],
    issue: ['read', 'create'],
    work_order: READ,
    work_order_line: ['read', 'update'],
    task_type: READ,
  },

  // Read-only. Also the shape a future investor portal takes.
  VIEWER: Object.fromEntries(
    RESOURCES.map((r) => [r, r === 'audit_log' || r === 'user' ? NONE : READ]),
  ) as Matrix['VIEWER'],
};

/** Roles whose grants span the whole tenant rather than assigned sites only. */
const TENANT_SCOPED_ROLES: ReadonlySet<Role> = new Set<Role>(['OWNER', 'ADMIN']);

export function scopeOf(role: Role): ScopeKind {
  return TENANT_SCOPED_ROLES.has(role) ? 'tenant' : 'site';
}

/** Does `role` grant `action` on `resource`? Site scope is checked separately. */
export function can(role: Role, resource: Resource, action: Action): boolean {
  return MATRIX[role]?.[resource]?.includes(action) ?? false;
}

/** Every action a role may take on a resource — used to drive UI affordances. */
export function allowedActions(role: Role, resource: Resource): readonly Action[] {
  return MATRIX[role]?.[resource] ?? NONE;
}

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

/** Ranking for "MANAGER or above" style checks. Higher is more privileged. */
const RANK: Record<Role, number> = {
  VIEWER: 0,
  FIELD_WORKER: 1,
  AGRONOMIST: 2,
  MANAGER: 3,
  ADMIN: 4,
  OWNER: 5,
};

export function atLeast(role: Role, minimum: Role): boolean {
  return RANK[role] >= RANK[minimum];
}

/** The subject of an authorisation decision. */
export interface Principal {
  userId: string;
  tenantId: string;
  role: Role;
  /** Site ids from `user_site`. Ignored for tenant-scoped roles. */
  siteIds: readonly string[];
}

/**
 * Site-scope check, independent of the role matrix. A tenant-scoped role
 * reaches every site; everyone else reaches only their assignments.
 *
 * `siteId` of `null` means the request is not site-specific (a tenant-level
 * catalogue, say), which every role may reach if the matrix allowed it.
 */
export function canAccessSite(principal: Principal, siteId: string | null): boolean {
  if (siteId === null) return true;
  if (scopeOf(principal.role) === 'tenant') return true;
  return principal.siteIds.includes(siteId);
}

export interface AuthorizeResult {
  ok: boolean;
  /** Distinguishes "your role cannot do this" from "not at this site". */
  reason?: 'role' | 'site';
}

/** The single decision function. Both checks, in the right order. */
export function authorize(
  principal: Principal,
  resource: Resource,
  action: Action,
  siteId: string | null = null,
): AuthorizeResult {
  if (!can(principal.role, resource, action)) return { ok: false, reason: 'role' };
  if (!canAccessSite(principal, siteId)) return { ok: false, reason: 'site' };
  return { ok: true };
}
